import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { IInterviewer } from './interviewer.models';
import { LoadingState } from '@tsin-core/entities';
import { AddInterviewer, GetInterviewers, SetInterviewerSearch, UpdateInterviewer } from './interviewer.actions';
import { tap, catchError } from 'rxjs';
import { InterviewerService } from '@tsin-core/services/http/interviewer.service';
import { UserRoleEnum } from '@tsin-shared/enums/UserRoleEnum';

export class InterviewerStateModel {
  public searchQuery!: String;
  public interviewers!: IInterviewer[];
  public loading!: LoadingState;
}

const defaults = {
  searchQuery: '',
  interviewers: [],
  loading: LoadingState.notLoading,
};

@State<InterviewerStateModel>({
  name: 'interviewerState',
  defaults
})

@Injectable()
export class InterviewerState {

  interviewerService: InterviewerService = inject(InterviewerService);

  @Selector()
  static getInterviewers(state: InterviewerStateModel) {
    return state.interviewers;
  }

  @Selector()
  static getLoading(state: InterviewerStateModel) {
    return state.loading;
  }

  @Selector()
  static filteredItems(state: InterviewerStateModel) {
      const query = state.searchQuery;
      if (!query || query === undefined) {
          return state.interviewers;  // Return all interviewers when searchQuery is empty
      }
      return state.interviewers.filter((interviewer: any) =>
          interviewer?.firstName?.toLowerCase().includes(query.toLowerCase()) ||  // Include partial matches
          interviewer?.lastName?.toLowerCase().includes(query.toLowerCase()) ||  // Include partial matches
          interviewer?.email?.toLowerCase().includes(query.toLowerCase()) // Include partial matches
      );
  }

  @Action(GetInterviewers)
  getInterviewers({ patchState }: StateContext<InterviewerStateModel>, action: GetInterviewers) {
    patchState({ loading: LoadingState.loadingList });
    return this.interviewerService.getInterviewers().pipe(
      tap((interviewers: IInterviewer[] ) => {
        const filteredinterviewers = interviewers.filter((interviewer: IInterviewer) => interviewer.role === UserRoleEnum.PRO_INTERVIEW);
        patchState({ interviewers: filteredinterviewers, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(AddInterviewer)
  addInterviewer(ctx: StateContext<InterviewerStateModel>, action: AddInterviewer) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.interviewerService.createInterviewer(action.payload).pipe(
      tap((result: IInterviewer) => {
        // console.log('Create Interviewer >>', result);
        const state = ctx.getState();
        ctx.patchState({
          interviewers: [...state.interviewers, result],
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        return error;
      })
    );
  }


  @Action(UpdateInterviewer)
  updateInterviewer(ctx: StateContext<InterviewerStateModel>, action: UpdateInterviewer) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.interviewerService.updateInterviewer(action.payload).pipe(
      tap((result:IInterviewer) => {
        // console.log('UPDATE STATUS INTERVIEWER>>', result);
        let interviewers = ctx.getState().interviewers;
        const index = interviewers.findIndex((interviewer: IInterviewer) => interviewer.id === result.id);
        interviewers[index] = result;
        ctx.patchState({
          interviewers,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        return error;
      })
    );
  }


  // Action to set the search query
  @Action(SetInterviewerSearch)
  setSearchQuery(ctx: StateContext<InterviewerStateModel>, action: SetInterviewerSearch) {
      ctx.patchState({ loading: LoadingState.loadingList });
      setTimeout(() => {
          ctx.patchState({
              searchQuery: action.query,
              loading: LoadingState.notLoading
          });
      }, 1000)
  }
}
